import React, {FunctionComponent} from "react";
import {useLingui} from "@lingui/react";
import {useWhiteLabelSetting} from "components/WhiteLabelProvider";
import {t, Trans} from "@lingui/macro";
import "./taxCalculationResult.css";
import {CalcJsonResult, emptyCalcResult, fetchResult, RubriekOptimalisatieInfo} from "./taxCalculationApi";
import {useAangifte} from "components/aangifte/AangifteProvider";
import {useTranslate} from "../hooks/useTranslate";
import {format, parse} from 'date-fns'
import {AJR, IJR} from "../../../settings";
import {Rubriek} from "../../../domain/store/rubriekenReducer";
import {formatAmount, formatCurrency, formatNumber, formatPct} from "../../../utils/format-utils";

export interface Props {
}

export const TaxCalculationResult: FunctionComponent<Props> = () => {
    const {aangifte} = useAangifte();
    const {i18n} = useLingui();
    const {locale} = i18n;
    const company = useWhiteLabelSetting("name") as string;
    const pensionLink = useWhiteLabelSetting("pensionLink") as string;
    const insuranceLink = useWhiteLabelSetting("insuranceLink") as string;
    const [calcResult, setCalcResult] = React.useState<CalcJsonResult>(emptyCalcResult);
    const [hasBesparing, setHasBesparing] = React.useState<boolean>(false);
    const [gebDatumAangever, setGebDatumAangever] = React.useState<string>();
    const [gebDatumPartner, setGebDatumPartner] = React.useState<string>();
    const [gemAvVjAangever, setGemAvVjAangever] = React.useState<number>();
    const [gemAvVjPartner, setGemAvVjPartner] = React.useState<number>();
    const printDisclaimer = useWhiteLabelSetting("printDisclaimer");
    const translate = useTranslate();

    const formatRubriek = (rubriek: Rubriek | undefined, value: number) => {
        switch (rubriek?.type) {
            case "bool":
                if (rubriek.default === "yes") {
                    return <Trans>Ja</Trans>;
                } else if (rubriek.default === "no") {
                    return <Trans>Nee</Trans>;
                }
                return value;
            case "num":
                return formatAmount(Number(value));
            case "date":
                const x = parse(value.toString().padStart(8,"0"), "ddMMyyyy", new Date());
                return format(x, "dd/MM/yyyy");
            case "pct":
            default:
                return formatNumber(Number(value / 100));
        }
    };

    React.useEffect(() => {
        const doFetch = async () => {
            const JSONResultString = await fetchResult({
                company,
                locale: locale as "nl" | "fr",
                aangifte,
            });
            const jsonResult: CalcJsonResult = JSONResultString === "" ? emptyCalcResult : JSON.parse(JSONResultString);
            setCalcResult(jsonResult);
            setHasBesparing(jsonResult.BesparingPensioensparen > 0 || jsonResult.BesparingLangeTermijn > 0);

            setGebDatumAangever(aangifte.rubrieken.get("3004")?.value as string);
            setGebDatumPartner(aangifte.rubrieken.get("3005")?.value as string);

            setGemAvVjAangever(aangifte.rubrieken.get("3006")?.value as number);
            setGemAvVjPartner(aangifte.rubrieken.get("3007")?.value as number);
        };
        doFetch();

    }, [aangifte, company, locale, pensionLink, insuranceLink, setCalcResult, setHasBesparing, setGebDatumAangever, setGebDatumPartner, setGemAvVjAangever, setGemAvVjPartner]);

    if (!calcResult) {
        return null;
    }

    const Besparing = () => (
        <div className="block" id="block3">
            <div className="outputlijn comment">
                <a href={insuranceLink} target="_blank" rel="noreferrer">
                    <u><Trans>Levensverzekering(en) voor uw maximaal aftrekbaar bedrag levert u {formatCurrency(calcResult.BesparingLangeTermijn)} extra belastingbesparing op.</Trans></u>
                </a>
                <br /><br />
                <a href={pensionLink} target="_blank" rel="noreferrer">
                    <u><Trans>Pensioensparen voor het maximumbedrag levert u {formatCurrency(calcResult.BesparingPensioensparen)} extra belastingbesparing op.</Trans></u>
                </a><br />
            </div>
        </div>
    );

    const Tips = () => (
        <div>
            <div id="tipstitel"><Trans>Tips om minder belastingen te betalen</Trans></div>
            <p>
                <a href={insuranceLink} target="_blank" rel="noreferrer">
                    <Trans>{formatCurrency(calcResult.BesparingLangeTermijn)} besparing door een levensverzekering af te sluiten</Trans>
                </a>
            </p>
            <p>
                <a href={pensionLink} target="_blank" rel="noreferrer">
                    <Trans>{formatCurrency(calcResult.BesparingPensioensparen)} besparing door pensioensparen</Trans>
                </a>
            </p>
        </div>
    );

    const OptimalisatieMogelijkLtsBsKap = () => (
        <div>
            <div className="outputlijn subtitle">
                <p className="optimalisatie"><Trans>Als de lening hoofdelijk en onverdeeld aan beide echtgenoten is toegestaan</Trans></p>
                <p className="optimalisatie"><Trans>mag de kapitaalaflossing vrij onder de echtgenoten worden verdeeld.</Trans></p>
                <p className="optimalisatie"><Trans>Bovendien moet aan een van de volgende voorwaarden voldaan zijn :</Trans></p>
                <p className="optimalisatie"><Trans>- Het is voor beide echtgenoten de enige woning.</Trans></p>
                <p className="optimalisatie"><Trans>- Het is voor beide echtgenoten niet de enige woning.</Trans></p>
                <p className="optimalisatie"><Trans>- Ofwel is het voor een van de echtgenoten de enige woning maar beiden</Trans></p>
                <p className="optimalisatie"><Trans>maken aanspraak op de vermindering voor het lange termijnsparen.</Trans></p>
            </div>
        </div>
    );

    const OptimalisatieUitgevoerd = () => (
        <div>
            <div className="outputlijn subtitle">
                <p className="optimalisatie"><Trans>De aangifte werd geoptimaliseerd !</Trans></p>
                <p className="optimalisatie"><Trans>Van volgende aangiftecodes werden de bedragen gewijzigd.</Trans></p>
                <p className="optimalisatie"><Trans>Denk er aan om deze bedragen ook in te vullen op uw aangifteformulier.</Trans></p>
            </div>
        </div>
    );

    const RubriekOptimalisatieInfo = (r:RubriekOptimalisatieInfo) => (
        <div>
            <div className="descr code">&nbsp;</div>
            <div className="opt1 code">&nbsp;</div>
            <div className="opt2 code">{r.Code}&nbsp;</div>
            <div className="amnt1 code">{formatNumber(r.NonOptimized)}&nbsp;</div>
            <div className="amnt2 code">{formatNumber(r.Optimized)}&nbsp;</div>
        </div>
    );

    const OptimalisatieInfo = () => (
        <div className="block" id="block7">
            { calcResult?.OptimalisatieInfo?.OptimalisatieUitgevoerd ? <OptimalisatieUitgevoerd/> : null}
            { !calcResult?.OptimalisatieInfo?.OptimalisatieUitgevoerd && calcResult?.OptimalisatieInfo?.OptimalisatieBsLtsKapMogelijk ? <OptimalisatieMogelijkLtsBsKap/> : null}

            <div className="descr code">&nbsp;</div>
            <div className="opt1 code">&nbsp;</div>
            <div className="opt2 code">&nbsp;</div>
            <div className="amnt1 code"><b><Trans>Ingevoerde bedragen</Trans></b>&nbsp;</div>
            <div className="amnt2 code"><b><Trans>Optimale verdeling</Trans></b>&nbsp;</div>

            {calcResult?.OptimalisatieInfo?.RubriekOptimalisaties.map(RubriekOptimalisatieInfo)}
        </div>
    );

    const Comment = (opmerking:string) => (
        <div className="outputlijn comment">{opmerking.split("\n").map((opmerking: string) => (
            <span key={opmerking}>{opmerking} <br/></span>
        ))}</div>
    );

    const Opmerkingen = () => (
        <div>
            <div className="outputlijn subtitle" style={{color:"#ff0000"}}><b><Trans>OPMERKINGEN</Trans></b>&nbsp;</div>
            {calcResult.Opmerkingen.map(Comment)}
        </div>
    );

    const BerekeningOnmogelijk = () => (
        <div style={{color:"#ff0000"}}>
            <div className="outputlijn subtitle"><b><Trans>BEREKENING ONMOGELIJK (err={calcResult.ErrorCode})</Trans></b>&nbsp;</div>
            {calcResult.ValidatieFouten.map(Comment)}
        </div>
    );

    const VoorafBetaling = (code: string, value: string) => (
        <span key={code}>
            <div className="codelijn">{code.startsWith("1") ? "VA" + (code.charAt(3)) : ""}</div>
            <div className="codelijn">{value}</div>
        </span>
    );

    const VoorafBetalingen = () => (
        <div>
            {calcResult.VoorafBetalingen
                ? Object.entries(calcResult.VoorafBetalingen)
                    .map( ([key, value]) => VoorafBetaling(key, value) )
                : null
            }
        </div>
    );

    const Rubriek = (code:string, value:any) => (
        <span key={code}>
              <div className="codelijnkader">{code}&nbsp;</div>
              <div className="codelijnkader">{value}</div>
        </span>
    );

    const Rubrieken = () => (
        <div>
            {
                calcResult.Rubrieken
                    ? Object.entries(calcResult.Rubrieken)
                        .map(([code, value]) => {
                            const rubriekInfo = aangifte.rubrieken.get(code);
                            return rubriekInfo === undefined ? null : Rubriek(code, formatRubriek(rubriekInfo, value))
                        })
                    : null
            }
            {
                calcResult.RubriekenVrijgesteld && Object.entries(calcResult.RubriekenVrijgesteld).length > 0
                    ?
                        <div>
                            <div className="outputlijn subtitle"><b><Trans>Vrijstelling met progressievoorbehoud : </Trans></b>&nbsp;</div>
                            {Object.entries(calcResult.RubriekenVrijgesteld)
                                .map(([code, value]) => {
                                    const rubriekInfo = aangifte.rubrieken.get(code);
                                    return rubriekInfo === undefined ? null : Rubriek(code, formatRubriek(rubriekInfo, value * 100.0))
                                })}
                        </div>
                    : null
            }
            {
                calcResult.RubriekenVerlaagd && Object.entries(calcResult.RubriekenVerlaagd).length > 0
                    ?
                    <div>
                        <div className="outputlijn subtitle"><b><Trans>Vermindering tot de helft  : </Trans></b>&nbsp;</div>
                        {Object.entries(calcResult.RubriekenVerlaagd)
                            .map(([code, value]) => {
                                const rubriekInfo = aangifte.rubrieken.get(code);
                                return rubriekInfo === undefined ? null : Rubriek(code, formatRubriek(rubriekInfo, value * 100.0))
                            })}
                    </div>
                    : null
            }

        </div>
    );

    const IngevuldeRubrieken = () => (
        <div>
            <div className="outputlijn comment">&nbsp;</div>
            { calcResult.Rubrieken && Object.keys(calcResult.Rubrieken).length > 0 ? <div className="outputlijn subtitle"><b><Trans>Ingevulde rubrieken : </Trans></b>&nbsp;</div> : null}
            {Comment(calcResult.AangifteInfo ? calcResult.AangifteInfo.join("\n") : "")}
            <VoorafBetalingen />
            <Rubrieken />
        </div>
    );

    const Hoofding = () => (
        <div className="block" id="block1">
            <div className="outputlijn comment"><b></b><p style={{textAlign:"right"}} ><b><Trans>AANSLAGJAAR {AJR} - INKOMSTEN VAN {IJR} (Versie : {calcResult.Versie})</Trans></b>&nbsp;</p></div>
            <div className="outputlijn town"><Trans>Gemeente : {aangifte?.city?.postalcode} {aangifte?.city ? translate(aangifte.city.name) : ""}</Trans>&nbsp;</div>
            {
                gebDatumAangever || gebDatumPartner
                    ?
                        <div className="outputlijn bday">
                            <Trans>Geboortedatum : </Trans>&nbsp;
                            { gebDatumAangever ? <Trans>Aangever : {gebDatumAangever} </Trans> : null}
                            { gebDatumAangever && gebDatumPartner  ? " - " : null}
                            { gebDatumPartner ? <Trans>Partner : {gebDatumPartner} </Trans> : null}
                        </div>
                    : null
            }
            <div className="outputlijn ttax"><Trans>Gemeentebelasting : {formatPct(aangifte.city?.taxRate ? aangifte.city.taxRate/100 : 0)}</Trans>&nbsp;</div>
            <div className="outputlijn date"><Trans>Datum berekening : {format(new Date(), "d/M/yyyy")}</Trans>&nbsp;</div>
            {
                gemAvVjAangever || gemAvVjPartner
                ?
                    <div className="outputlijn averatio">
                        <Trans>Gemiddelde aanslagvoet vorig jaar :</Trans>&nbsp;
                        { gemAvVjAangever ? <span>{formatPct(gemAvVjAangever)}</span> : "?? %"}
                        { aangifte.isGemeenschappelijkeAangifte
                            ? <span>&nbsp;/&nbsp;{ gemAvVjPartner ? <span>{formatPct(gemAvVjPartner)} %</span> : "?? %"}</span>
                            : null
                        }

                    </div>
                    : null
            }

            {calcResult.Opmerkingen && calcResult.Opmerkingen.length > 0 ? <Opmerkingen /> : null}
            {calcResult.ErrorCode > 0 ? <BerekeningOnmogelijk /> : null}

            <IngevuldeRubrieken />
        </div>
    );

    const BerekeningResultaat = () => (
        <div>
            <div className="block" style={{margin: "0px 0px 5px 0px", padding: "0px"}} dangerouslySetInnerHTML={{__html: calcResult.TeBetalenBelastingHtml}}></div>
            { hasBesparing ? <Besparing /> : null }
            { calcResult?.OptimalisatieInfo?.OptimalisatieBsLtsKapMogelijk || calcResult?.OptimalisatieInfo?.OptimalisatieWoonBonusMogelijk ? <OptimalisatieInfo/> : null}
            <div dangerouslySetInnerHTML={{__html: calcResult.DetailBerekeningHtml}}></div>
        </div>
    );


    return (
        <>
            <div className="nobanner">
                { hasBesparing ? <Tips /> : null }
                <p></p>
            </div>
            <div className="main">
                <div id="page_result">
                    <div id="sofisk_output">
                        <Hoofding />
                        {calcResult.ErrorCode === 0
                            ? <BerekeningResultaat />
                            : <div className="outputlijn subtitle" style={{color:"#ff0000"}}><b><Trans>BEREKENING ONMOGELIJK (err={calcResult.ErrorCode})</Trans></b></div>}
                    </div>
                    <div className="printdisclaimer">{printDisclaimer}</div>
                    <div className="knoppen" style={{textAlign: "right"}}>
                        <button className="btn btn-default btn-print" onClick={window.print} title={i18n._(t`Afdrukken`)}>
                            <i className="fa fa-print fa-2x"/>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

TaxCalculationResult.displayName = "TaxCalculationResult";
